import type { Entry } from "./session.ts";

/**
 * The abs circuit: a few movements done in turn, round after round. Each
 * movement is its own line of the session; these helpers put them back
 * together and walk through them in the order they are actually done —
 * round 1 of every movement, then round 2, and so on.
 */

/** Between two rounds, when the sheet says nothing. */
export const ROUND_REST_SECONDS = 60;

/** What the session list shows: a movement, or a whole circuit as one row. */
export type Row =
  { kind: "single"; entry: Entry; index: number } | { kind: "circuit"; name: string; items: Entry[]; index: number };

export function groupRows(entries: Entry[]): Row[] {
  const rows: Row[] = [];
  entries.forEach((entry, index) => {
    const last = rows.at(-1);
    if (entry.circuitGroup !== null && last?.kind === "circuit" && last.name === entry.circuitGroup) {
      last.items.push(entry);
    } else if (entry.circuitGroup !== null) {
      rows.push({ kind: "circuit", name: entry.circuitGroup, items: [entry], index });
    } else {
      rows.push({ kind: "single", entry, index });
    }
  });
  return rows;
}

/**
 * Where position `index` stands among the session list's cards, 0-based.
 * Counting lines instead read "05 sur 8" on the last card of five.
 */
export function rowNumber(entries: Entry[], index: number): { number: number; total: number } {
  const rows = groupRows(entries);
  const number = rows.findIndex((row) =>
    row.kind === "single" ? row.index === index : index >= row.index && index < row.index + row.items.length,
  );
  return { number, total: rows.length };
}

/** The circuit around position `index`, by its first and last positions in the session. */
export function circuitAround(entries: Entry[], index: number): { items: Entry[]; first: number; last: number } | null {
  const group = entries[index]?.circuitGroup ?? null;
  if (group === null) return null;
  let first = index;
  while (entries[first - 1]?.circuitGroup === group) first -= 1;
  let last = index;
  while (entries[last + 1]?.circuitGroup === group) last += 1;
  return { items: entries.slice(first, last + 1), first, last };
}

export function roundsOf(items: Entry[]): number {
  return items[0]?.circuitRounds ?? items[0]?.targetSets ?? 1;
}

/** One movement, one round. */
export interface Step {
  round: number;
  item: number;
}

export const stepKey = (step: Step) => `${step.round}:${step.item}`;

/**
 * Done when a set carries that round's number. Counting sets would move a
 * skipped round 1 onto round 2's set after a reload. Ticking the movement as
 * done covers every round.
 */
export function isStepDone(item: Entry, round: number): boolean {
  return item.doneAt !== null || item.sets.some((set) => !set.isWarmup && set.setIndex === round);
}

/**
 * The next movement to do. `passed` holds the steps already dealt with on this
 * screen — skipped, or saved and not yet back from the server — so a slow
 * network never offers the same plank twice.
 */
export function nextStep(items: Entry[], passed: ReadonlySet<string> = new Set()): Step | null {
  const rounds = roundsOf(items);
  for (let round = 1; round <= rounds; round++) {
    for (let item = 0; item < items.length; item++) {
      if (!isStepDone(items[item]!, round) && !passed.has(stepKey({ round, item }))) return { round, item };
    }
  }
  return null;
}

/**
 * The countdown after a step: none between movements — only the time to get
 * in position — and the round's rest after the last one, unless it was the
 * last round.
 */
export function restAfterStep(items: Entry[], step: Step): number {
  if (step.item !== items.length - 1 || step.round >= roundsOf(items)) return 0;
  return items[step.item]!.restSeconds || ROUND_REST_SECONDS;
}

/** "10 reps", "1'", "30 s / côté". */
export function describeTarget(item: Entry): string {
  if (item.targetSeconds !== null) {
    const time = item.targetSeconds % 60 === 0 ? `${item.targetSeconds / 60}'` : `${item.targetSeconds} s`;
    return item.perSide ? `${time} / côté` : time;
  }
  return item.targetReps ? `${item.targetReps} reps` : "";
}
